// b20-1: temperaturen ute, samma värde överallt: platskortet, husets vy, Hälsa, kortet Vädret och
// förvärmningen. Ordningen:
//   1. väderstationens ute.temp, om värdet är högst 30 minuter gammalt;
//   2. annars husets egen givare hus.ute.temp, om den är högst 30 minuter gammal;
//   3. annars det nyaste av dem, märkt som gammalt;
//   4. aldrig prognosen. Prognosens temperatur visas bara under Prognos, och är märkt så.
//
// Källan följer värdet: navet skickar källan för ute.temp som ute.kalla@station och källan för
// hus.ute.temp som ute.kalla@hus. Förut skrevs båda som ute.kalla@temp, och källan på kortet kunde
// höra till det andra värdet. En gammal rad med ute.kalla@temp läses som förut, tills den har
// ersatts av den nya.

import type { Fynd } from "@koll/katalog";
import type { Signalvarde } from "./typer.ts";

/**
 * b20-3: en källa som ser ut som ett entitets-id ("sensor.ute_temperatur", eller "Home Assistant:
 * sensor.ute_temperatur" som navet skickade före b20-3). Ett entitets-id får inte visas och lämnar
 * aldrig navet; en sådan källa visas som "Home Assistant" tills navet har skickat en ny.
 */
const ENTITETSID = /^(?:Home Assistant:\s*)?[a-z_]+\.[a-z0-9_]+$/;

/**
 * b21-1 (PL6/PL7): ett givarnamn från före b21-1 med slutet av MAC-adressen: "Ruuvi A1B2". Märkena
 * är de som givarnamn ger. Det nya namnet ("Ruuvi (a3f9)") har parenteser och matchar aldrig.
 */
export const MAC_SLUT_NAMN = /^(Ruuvi|Shelly|Xiaomi|Inkbird|BTHome) [0-9A-Fa-f]{4}$/;

/** Samma mönster som MAC_SLUT_NAMN för SQL (Postgres, POSIX), för migreringen 024. */
export const MAC_SLUT_NAMN_SQL = "^(Ruuvi|Shelly|Xiaomi|Inkbird|BTHome) [0-9A-Fa-f]{4}$";

/** Ett givarnamn utan MAC-slut: "Ruuvi A1B2" blir "Ruuvi". Annan text lämnas som den är. */
export function tvattaMacSlutNamn<T extends string | null>(text: T): T | string {
  if (typeof text !== "string") return text;
  const t = text.trim();
  return MAC_SLUT_NAMN.test(t) ? t.slice(0, t.indexOf(" ")) : text;
}

/** Samma mönster mitt i en text (en rad i loggen): "Ruuvi A1B2 hördes" blir "Ruuvi hördes". */
export function tvattaMacSlutText(text: string): string {
  return text.replace(/\b(Ruuvi|Shelly|Xiaomi|Inkbird|BTHome) [0-9A-Fa-f]{4}\b/g, "$1");
}

/** b21-1 (PL6/PL7): ett namn "Ruuvi A1B2" i ett fynd blir "Ruuvi" (samma mönster som migreringen 024). */
export function tvattaMacSlutFynd(f: Fynd): Fynd {
  const ut: Fynd = { ...f };
  if (f.ha && typeof f.ha.namn === "string")
    ut.ha = { ...f.ha, namn: tvattaMacSlutNamn(f.ha.namn) };
  if (f.homey && typeof f.homey.namn === "string") {
    ut.homey = { ...f.homey, namn: tvattaMacSlutNamn(f.homey.namn) };
  }
  return ut;
}

/**
 * Källan i ord, utan entitets-id och utan MAC-slut: ett id blir "Home Assistant", och ett gammalt
 * givarnamn "Ruuvi A1B2" blir "Ruuvi" (b21-1, tills alla nav är uppdaterade).
 */
export function tvattaUtekalla<T extends string | null>(text: T): T | string {
  if (typeof text !== "string") return text;
  if (ENTITETSID.test(text.trim())) return "Home Assistant";
  return tvattaMacSlutNamn(text);
}

/** Ett värde ute är färskt i högst så här lång tid. */
export const UTE_FARSK_MS = 30 * 60_000;

/** Var temperaturen ute kommer ifrån: väderstationen (ute.temp) eller huset (hus.ute.temp). */
export type Utekalla = "station" | "hus";

export interface Utetemp {
  varde: number;
  tid: number;
  kalla: Utekalla;
  /** Värdet är äldre än 30 minuter: inget färskt fanns. */
  gammalt: boolean;
  /** Källan i ord, till exempel "Ecowitt GW2000 via Home Assistant", när navet säger den. */
  text: string | null;
}

/** Signalerna för temperaturen ute, som platsens senaste värden, med signal@instans som nyckel. */
export type Utesignaler = Readonly<Record<string, Signalvarde | undefined>>;

const SIGNAL: Record<Utekalla, string> = { station: "ute.temp", hus: "hus.ute.temp" };

/**
 * Instansen för ute.kalla när navet skickar en signal: station för ute.temp, hus för hus.ute.temp
 * och fukt för ute.fukt. Null för andra signaler.
 */
export function utekallaInstans(signal: string): "station" | "hus" | "fukt" | null {
  if (signal === "ute.temp") return "station";
  if (signal === "hus.ute.temp") return "hus";
  if (signal === "ute.fukt") return "fukt";
  return null;
}

const tal = (s: Signalvarde | undefined): { varde: number; tid: number } | null =>
  s && typeof s.varde === "number" && Number.isFinite(s.varde)
    ? { varde: s.varde, tid: s.tid }
    : null;

const textAv = (s: Signalvarde | undefined): string | null =>
  s && typeof s.varde === "string" && s.varde.trim() ? s.varde : null;

/** Källan i ord för ett värde: ute.kalla@station eller @hus, annars den gamla ute.kalla@temp. */
export function utekallaText(s: Utesignaler, kalla: Utekalla): string | null {
  return tvattaUtekalla(textAv(s[`ute.kalla@${kalla}`]) ?? textAv(s["ute.kalla@temp"]));
}

/**
 * Väljer källan efter tiderna för det senaste värdet från stationen och från huset: stationen om
 * den är färsk, annars huset om det är färskt, annars det nyaste. Null utan något värde.
 */
export function valjUtekalla(
  station: number | null,
  hus: number | null,
  nu: number,
): { kalla: Utekalla; gammalt: boolean } | null {
  if (station !== null && nu - station <= UTE_FARSK_MS) return { kalla: "station", gammalt: false };
  if (hus !== null && nu - hus <= UTE_FARSK_MS) return { kalla: "hus", gammalt: false };
  if (station === null && hus === null) return null;
  const kalla = hus === null || (station !== null && station >= hus) ? "station" : "hus";
  return { kalla, gammalt: true };
}

/** Temperaturen ute nu, med tiden och källan. Null utan något värde. Aldrig prognosen. */
export function uteTemp(s: Utesignaler, nu: number): Utetemp | null {
  const station = tal(s[SIGNAL.station]);
  const hus = tal(s[SIGNAL.hus]);
  const val = valjUtekalla(station?.tid ?? null, hus?.tid ?? null, nu);
  if (!val) return null;
  const v = (val.kalla === "station" ? station : hus) as { varde: number; tid: number };
  return {
    varde: v.varde,
    tid: v.tid,
    kalla: val.kalla,
    gammalt: val.gammalt,
    text: utekallaText(s, val.kalla),
  };
}

/**
 * En serie för temperaturen ute, till exempel för värmemodellen: stationens värden, och husets
 * där stationen inte har något värde inom 30 minuter. Samma ordning som uteTemp, värde för värde.
 */
export function uteSerie<T extends { tid: number }>(station: readonly T[], hus: readonly T[]): T[] {
  if (station.length === 0) return [...hus];
  const tider = station.map((x) => x.tid).sort((a, b) => a - b);
  const narmast = (t: number) => {
    let lo = 0;
    let hi = tider.length - 1;
    while (lo < hi) {
      const mitt = (lo + hi) >> 1;
      if ((tider[mitt] as number) < t) lo = mitt + 1;
      else hi = mitt;
    }
    const efter = tider[lo] as number;
    const fore = lo > 0 ? (tider[lo - 1] as number) : efter;
    return Math.min(Math.abs(efter - t), Math.abs(t - fore));
  };
  const ut = [...station, ...hus.filter((x) => narmast(x.tid) > UTE_FARSK_MS)];
  return ut.sort((a, b) => a.tid - b.tid);
}
